import { call, takeEvery, takeLatest, put, select } from 'redux-saga/effects';
import * as commonTypes from '@store/common/types';
import {
  createFilter,
  createFilterForAppExperience,
  updateFilterForAppExperience,
  getFilters,
  getFiltersForAppExperience,
  updateFilter,
  deleteFilterForAppExperience,
} from '@api/filters';
import { message } from 'antd';
import get from 'lodash/get';
import { categoryMap } from '@components/filter-panel/constants';
import { isAppModule } from '@utils/constants';
import * as types from './types';
import { isCacheValidSelector, filtersSelector } from './selectors';
import { clearFilterCache } from './actions';

function handleErrorResp(err) {
  const { response } = err;
  // For v2 api
  const errorMsg = get(response, 'data.error', '') || get(response, 'data.message', '');
  if (errorMsg) {
    message.warning(errorMsg);
    return;
  }
  if (response) {
    const { data, statusText, status } = response;

    if (status === 403) {
      if (data.fv) {
        const { fv } = data;
        message.warning(`Filter with same rule found: ${fv.name}`);
      }
    } else if (data) {
      const errorMsgs = Object.values(data)[0] as string | string[];
      message.warning(Array.isArray(errorMsgs) ? errorMsgs.join('') : errorMsgs);
    } else if (statusText) {
      message.warning(statusText);
    }
  }
}

export function* create(action) {
  try {
    yield put({ type: types.START_SAVING_CHANGES });
    const resp = yield call(createFilter, action.payload);
    const { name } = resp.data;
    yield put({ type: types.GET_FILTERS });
    message.info(`Filter ${name} created`);
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* apply(action) {
  try {
    yield put({
      type: commonTypes.APPLY_FILTER,
      filter: action.payload,
    });
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({
      type: types.CHANGE_FILTER_CONTAINER_VISIBILITY,
      visible: false,
    });
  }
}

export function* createAndApply(action) {
  try {
    const createFilterMethod = isAppModule ? createFilterForAppExperience : createFilter;

    yield put({ type: types.START_SAVING_CHANGES });
    const resp = yield call(createFilterMethod, action.payload);
    const { name, id } = resp.data;
    message.info(`Filter ${name} created`);
    const { rule } = action.payload;
    yield put({
      type: types.CHANGE_FILTER_CONTAINER_VISIBILITY,
      visible: false,
    });
    yield put({
      type: commonTypes.APPLY_FILTER,
      filter: {
        name,
        rule,
        fid: id,
      },
    });
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* deleteFilter(action) {
  try {
    const filters = yield select(filtersSelector);
    yield call(deleteFilterForAppExperience, action.id);
    const updatedFilters = filters.filter((filter) => filter.id !== action.id);

    yield put({ type: types.DONE_GETTING_FILTERS, filters: updatedFilters });
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* update(action) {
  try {
    const updateFilterMethodFilterMethod = isAppModule
      ? updateFilterForAppExperience
      : updateFilter;
    yield put({ type: types.START_SAVING_CHANGES });
    const resp = yield call(updateFilterMethodFilterMethod, action.fid, action.payload);

    const { fid = action.fid, name, rule } = resp.data;
    yield put({ type: types.GET_FILTERS });
    message.info(`Filter ${name} updated`);

    if (isAppModule) {
      yield put({
        type: types.CHANGE_FILTER_CONTAINER_VISIBILITY,
        visible: false,
      });

      const state = yield select();
      const selectedFilters = get(state.filterContainer, 'savedFilterPanel.selectedFilters', []);
      // need to update the filter state to trigger widget refresh when the filter is already applied
      const isApplied = selectedFilters.some((it) => it.fid === fid);

      if (isApplied) {
        yield put({
          type: commonTypes.APPLY_FILTER,
          filter: {
            name,
            rule,
            fid,
          },
        });
      }
    }
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* updateAndApply(action) {
  try {
    yield put({ type: types.START_SAVING_CHANGES });
    const updateFilterMethodFilterMethod = isAppModule
      ? updateFilterForAppExperience
      : updateFilter;

    const resp = yield call(updateFilterMethodFilterMethod, action.fid, action.payload);
    const { name } = resp.data;
    message.info(`Filter ${name} updated`);
    const { rule } = action.payload;
    yield put({
      type: types.CHANGE_FILTER_CONTAINER_VISIBILITY,
      visible: false,
    });
    yield put({
      type: commonTypes.APPLY_FILTER,
      filter: {
        name,
        rule,
        fid: action.fid,
      },
    });
  } catch (err) {
    handleErrorResp(err);
  } finally {
    yield put({ type: types.DONE_SAVING_CHANGES });
  }
}

export function* getAllFilters(action) {
  try {
    const getFiltersMethod = isAppModule ? getFiltersForAppExperience : getFilters;

    // check for cached filter list
    const isCacheValid = yield select(isCacheValidSelector);
    if (isCacheValid) {
      return;
    }

    const resp = yield call(getFiltersMethod);
    yield put({ type: types.DONE_GETTING_FILTERS, filters: resp });
  } catch (err) {
    const { response } = err;
    if (response) {
      // must ensure the messages exist,
      // or the null value will occur an error and catch all the actions of rootsaga
      const { data = {} } = response;
      if (typeof data === 'string') {
        message.warning(data);
      } else {
        const messages = get(Object.values(data), [0], []);
        message.warning(messages.join(''));
      }
    }
  }
}

function* deleteFilterSaga() {
  yield takeLatest(types.DELETE_FILTER, deleteFilter);
}

function* applyFilterSaga() {
  yield takeLatest(types.APPLY_FILTER, apply);
}

function* createFilterSaga() {
  yield takeEvery(types.CREATE_FILTER, create);
}

function* createAndApplyFilterSaga() {
  yield takeEvery(types.CREATE_AND_APPLY, createAndApply);
}

function* updateFilterFilterSaga() {
  yield takeEvery(types.UPDATE_FILTER, update);
}

function* updateAndApplyFilterFilterSaga() {
  yield takeEvery(types.UPDATE_AND_APPLY, updateAndApply);
}

// clear filter cache on any action which updates filters
function* clearFilterCacheSaga() {
  yield takeEvery(
    [types.CREATE_FILTER, types.CREATE_AND_APPLY, types.UPDATE_FILTER, types.UPDATE_AND_APPLY],
    function* clearCache() {
      yield put(clearFilterCache());
    },
  );
}

function* getFiltersSaga() {
  yield takeLatest(types.GET_FILTERS, getAllFilters);
}

function* updateCategoryAndSubcategory() {
  yield takeEvery(
    [
      types.ADD_AND_RULE,
      types.ADD_OR_RULE,
      types.UPDATE_OR_RULE,
      types.DELETE_OR_RULE,
      types.CHANGE_FILTER_CONTAINER_VISIBILITY,
    ],
    function* listen() {
      const state = yield select();
      const { visible } = state.filterContainer;
      if (visible) {
        const { rule } = state.filterContainer.newFilterPanel;
        const { field } = rule[0][0];
        const option = categoryMap.get(field);
        if (option) {
          yield put({
            type: types.UPDATE_FILTER_CONDITIONS,
            payload: { ...option },
          });
        }
      }
    },
  );
}

function* changeFilterContainerVisibility() {
  yield takeEvery(types.CHANGE_FILTER_CONTAINER_VISIBILITY, function* resetFilterPanel() {
    const state = yield select();
    const { visible, type } = state.filterContainer;

    // when panel is invisible under edit mode
    if (!visible && type === 'edit') {
      yield put({
        type: types.STOP_EDIT_FILTER,
      });
    }
  });
}

const filterContainerSagas = [
  clearFilterCacheSaga,
  applyFilterSaga,
  createFilterSaga,
  createAndApplyFilterSaga,
  updateFilterFilterSaga,
  updateAndApplyFilterFilterSaga,
  getFiltersSaga,
  updateCategoryAndSubcategory,
  deleteFilterSaga,
  changeFilterContainerVisibility,
];

export default filterContainerSagas;
